import { createApi, fetchBaseQuery } from "@reduxjs/toolkit/query/react";

const WEATHER_API_KEY = "0f5ebeeb0484c6b18481a46bbeebb564";

export interface WeatherApiResponse {
  coord: {
    lon: number;
    lat: number;
  };
  weather: {
    id: number;
    main: string;
    description: string;
    icon: string;
  }[];
  base: string;
  main: {
    temp: number;
    feels_like: number;
    temp_min: number;
    temp_max: number;
    pressure: number;
    humidity: number;
    sea_level?: number;
    grnd_level?: number;
  };
  visibility: number;
  wind: {
    speed: number;
    deg: number;
    gust?: number;
  };
  clouds: {
    all: number;
  };
  dt: number;
  sys: {
    type?: number;
    id?: number;
    country: string;
    sunrise: number;
    sunset: number;
  };
  timezone: number;
  id: number;
  name: string;
  cod: number;
}

interface IpLocationApiResponse {
  ip: string;
  city: string;
  region: string;
  country: string;
  country_code: string;
  latitude: string;
  longitude: string;
  timezone: string;
}

interface LocationApiResponse {
  place_id: number;
  licence: string;
  osm_type: string;
  osm_id: number;
  lat: string;
  lon: string;
  class: string;
  type: string;
  place_rank: number;
  importance: number;
  addresstype: string;
  name: string;
  display_name: string;
  address: {
    road?: string;
    neighbourhood?: string;
    suburb?: string;
    city?: string;
    state?: string;
    country: string;
    country_code: string;
  };
  boundingbox: [string, string, string, string];
}

// Serializer to round coords and prevent micro-drift refetches
export const weatherApi = createApi({
  reducerPath: "weatherApi",
  baseQuery: fetchBaseQuery({ baseUrl: "/" }),
  serializeQueryArgs: ({ queryArgs, endpointName }) => {
    if (
      queryArgs &&
      typeof queryArgs === "object" &&
      "lat" in queryArgs &&
      "lon" in queryArgs
    ) {
      const { lat, lon } = queryArgs as { lat: number; lon: number };
      const roundedLat = Math.round(lat * 100) / 100;
      const roundedLon = Math.round(lon * 100) / 100;
      return `${endpointName}(${roundedLat},${roundedLon})`;
    }
    return endpointName;
  },
  endpoints: (builder) => ({
    getWeatherByCoords: builder.query<
      WeatherApiResponse,
      { lat: number; lon: number }
    >({
      query: ({ lat, lon }) =>
        `https://api.openweathermap.org/data/2.5/weather?lat=${lat}&lon=${lon}&units=metric&appid=${WEATHER_API_KEY}`,
      keepUnusedDataFor: 1800,
    }),
    getLocationByCoords: builder.query<
      LocationApiResponse,
      { lat: number; lon: number }
    >({
      query: ({ lat, lon }) =>
        `https://nominatim.openstreetmap.org/reverse?lat=${lat}&lon=${lon}&format=json`,
      keepUnusedDataFor: 1800,
    }),
    getIpLocation: builder.query<IpLocationApiResponse, void>({
      query: () => `https://get.geojs.io/v1/ip/geo.json`,
      keepUnusedDataFor: 1800,
    }),
  }),
});

export const {
  useGetWeatherByCoordsQuery,
  useGetLocationByCoordsQuery,
  useGetIpLocationQuery,
} = weatherApi;
